
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { Partner, Shop, MembershipPlan, PartnerType } from '../domain/types';
import { partnerService } from '../domain/services/partnerService';
import { partnerRepository } from '../data/partnerRepository';
import { partnerNotificationRepository, type PartnerNotificationEntity as PartnerNotification } from '../data/partnerNotificationRepository';
import { useAuth } from './auth';
import { logger } from '../infrastructure/logger';

interface PartnerContextType {
  // Datos del partner
  partner: Partner | null;
  shop: Shop | null;
  isLoading: boolean;
  isPartner: boolean;

  // Registro
  registerAsPartner: (data: RegisterPartnerData) => Promise<{ success: boolean; error?: string }>;
  checkSlugAvailability: (slug: string) => Promise<{ available: boolean; error?: string }>;

  // Shop management
  updateShop: (updates: Partial<Shop>) => Promise<{ success: boolean; error?: string }>;
  refreshPartnerData: () => Promise<void>;

  // Followers
  followShop: (shopId: string) => Promise<void>;
  unfollowShop: (shopId: string) => Promise<void>;
  isFollowing: boolean;
  followersCount: number;
  
  // User's following shops
  followingShops: Shop[];
  followingShopIds: string[];
  refreshFollowingShops: () => Promise<void>;

  // Limits
  canAddProduct: boolean;
  productsLimit: number;
  commissionRate: number;
  
  // Notifications
  notifications: PartnerNotification[];
  unreadCount: number;
  notificationsLoading: boolean;
  refreshNotifications: () => Promise<void>;
  markNotificationAsRead: (notificationId: string) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
}

interface RegisterPartnerData {
    name: string;
    phone: string;
    dni: string;
    type: PartnerType;
    businessName?: string;
    membership: MembershipPlan;
    shopName: string;
    shopSlug: string;
    shopDescription: string;
    categories: string[];
    address?: string;
    whatsapp?: string;
}

const PartnerContext = createContext<PartnerContextType | null>(null);

export const PartnerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
const [partner, setPartner] = useState<Partner | null>(null);
  const [shop, setShop] = useState<Shop | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingShops, setFollowingShops] = useState<Shop[]>([]);
  const [followingShopIds, setFollowingShopIds] = useState<string[]>([]);
  
  // Notifications state
  const [notifications, setNotifications] = useState<PartnerNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

    // Verificar si el usuario es partner al cargar
    useEffect(() => {
        if (user) {
            loadPartnerData();
        } else {
            setPartner(null);
            setShop(null);
            setIsLoading(false);
        }
    }, [user]);

    const loadPartnerData = async () => {
        if (!user) return;
        
        setIsLoading(true);
        try {
            const partnerData = await partnerService.getPartnerByUserId(user.id);
            if (partnerData) {
                setPartner(partnerData);
                
                if (partnerData.shopId) {
                    try {
                        const shopData = await partnerRepository.getShopById(partnerData.shopId);
                        if (shopData) {
                            setShop(shopData);
                        }
                    } catch (shopError) {
                        // Silenciar errores del emulador para shop
                        const error = shopError as Error;
                        if (!error.message?.includes('permission-denied') && 
                            !error.message?.includes('Null value') &&
                            !error.message?.includes('INTERNAL ASSERTION')) {
                            logger.error("Error loading shop", error);
                        }
                    }
                }
            }
        } catch (error) {
            // Silenciar errores del emulador para partner
            const err = error as Error;
            if (!err.message?.includes('permission-denied') && 
                !err.message?.includes('Null value') &&
                !err.message?.includes('INTERNAL ASSERTION')) {
                logger.error("Error loading partner data", err);
            }
} finally {
      setIsLoading(false);
    }
  };

  // Notifications functions
  const refreshNotifications = useCallback(async () => {
    if (!partner) return;
    
    setNotificationsLoading(true);
    try {
      const [notifs, count] = await Promise.all([
        partnerNotificationRepository.getNotifications(partner.id, { limit: 20 }),
        partnerNotificationRepository.getUnreadCount(partner.id)
      ]);
      setNotifications(notifs);
      setUnreadCount(count);
      } catch (error) {
        logger.error("Error loading notifications", error instanceof Error ? error : new Error(String(error)));
      } finally {
      setNotificationsLoading(false);
    }
  }, [partner]);

  const markNotificationAsRead = useCallback(async (notificationId: string) => {
    const success = await partnerNotificationRepository.markAsRead(notificationId);
    if (success) {
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  }, []);

  const markAllNotificationsAsRead = useCallback(async () => {
    if (!partner) return;
    const success = await partnerNotificationRepository.markAllAsRead(partner.id);
    if (success) {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() })));
      setUnreadCount(0);
    }
  }, [partner]);

  // Poll for notifications when partner is loaded
  useEffect(() => {
    if (!partner) return;

    refreshNotifications();

    // Poll every 30 seconds to reduce Firebase reads
    const interval = setInterval(() => {
      refreshNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, [partner, refreshNotifications]);

  const registerAsPartner = useCallback(async (data: RegisterPartnerData): Promise<{ success: boolean; error?: string }> => {
        if (!user) return { success: false, error: "Debes iniciar sesión" };

        try {
            const result = await partnerService.registerPartnerAndShop(
                {
                    firebaseUid: user.id,
                    email: user.email,
                    name: data.name,
                    phone: data.phone,
                    dni: data.dni,
                    type: data.type,
                    businessName: data.businessName,
                    membership: data.membership
                },
                {
                    name: data.shopName,
                    slug: data.shopSlug,
                    description: data.shopDescription,
                    categories: data.categories,
                    contactInfo: {
                        phone: data.phone,
                        email: user.email,
                        whatsapp: data.whatsapp,
                        address: data.address
                    }
                }
            );

            if (result.success) {
                await loadPartnerData();
            }

            return result;
        } catch (error) {
            logger.error("Error registering partner", error instanceof Error ? error : new Error(String(error)));
            return { success: false, error: "Error al registrar" };
        }
    }, [user]);

    const checkSlugAvailability = useCallback(async (slug: string) => {
        return partnerService.checkSlugAvailability(slug);
    }, []);

    const updateShop = useCallback(async (updates: Partial<Shop>): Promise<{ success: boolean; error?: string }> => {
        if (!shop) return { success: false, error: "No hay tienda" };
        
        const result = await partnerService.updateShop({ shopId: shop.id, updates });
        if (result.success) {
            await loadPartnerData();
        }
        return result;
    }, [shop]);

    const refreshPartnerData = useCallback(async () => {
        await loadPartnerData();
    }, []);

    const followShop = useCallback(async (shopId: string) => {
        if (!user) return;
        await partnerService.followShop(shopId, user.id);
        setIsFollowing(true);
        setFollowersCount(prev => prev + 1);
    }, [user]);

    const unfollowShop = useCallback(async (shopId: string) => {
        if (!user) return;
        await partnerService.unfollowShop(shopId, user.id);
        setIsFollowing(false);
        setFollowersCount(prev => prev - 1);
    }, [user]);

    const refreshFollowingShops = useCallback(async () => {
        if (!user) return;
        try {
            const [shops, ids] = await Promise.all([
                partnerService.getShopsFollowingByUser(user.id),
                partnerService.getFollowingShopIds(user.id)
            ]);
            setFollowingShops(shops);
            setFollowingShopIds(ids);
        } catch (error) {
            logger.error('Error refreshing following shops', error instanceof Error ? error : new Error(String(error)));
        }
    }, [user]);

    // Load following shops on mount and when user changes
    useEffect(() => {
        if (user) {
            refreshFollowingShops();
        }
    }, [user, refreshFollowingShops]);

    // Computed values
    const isPartner = useMemo(() => !!partner, [partner]);
    
    const canAddProduct = useMemo(() => {
        if (!partner) return false;
        const limits = {
            free: 10,
            basic: 25,
            pro: 75,
            premium: -1
        };
        const limit = limits[partner.membership];
        return limit === -1 || (shop?.stats.totalProducts || 0) < limit;
    }, [partner, shop]);

    const productsLimit = useMemo(() => {
        if (!partner) return 0;
        const limits = { free: 10, basic: 25, pro: 75, premium: -1 };
        return limits[partner.membership];
    }, [partner]);

    const commissionRate = useMemo(() => {
        return partner?.commissionRate || 20;
    }, [partner]);

const value = useMemo(() => ({
    partner,
    shop,
    isLoading,
    isPartner,
    registerAsPartner,
    checkSlugAvailability,
    updateShop,
    refreshPartnerData,
    followShop,
    unfollowShop,
    isFollowing,
    followersCount,
    followingShops,
    followingShopIds,
    refreshFollowingShops,
    canAddProduct,
    productsLimit,
    commissionRate,
    notifications,
    unreadCount,
    notificationsLoading,
    refreshNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead
  }), [
    partner, shop, isLoading, isPartner, isFollowing, followersCount, followingShops, followingShopIds,
    canAddProduct, productsLimit, commissionRate, notifications, unreadCount, notificationsLoading,
    registerAsPartner, checkSlugAvailability, updateShop, refreshPartnerData,
    followShop, unfollowShop, refreshNotifications, markNotificationAsRead, markAllNotificationsAsRead
  ]);

    return (
        <PartnerContext.Provider value={value}>
            {children}
        </PartnerContext.Provider>
    );
};

export const usePartner = () => {
    const context = useContext(PartnerContext);
    if (!context) {
        throw new Error("usePartner must be used within a PartnerProvider");
    }
    return context;
};
